import { LinearPriceRowPlugin } from '@domains/subscription/priv/Billables/factories/LinearPricePluginFactory'
import { MaxPriceRowPlugin } from '@domains/subscription/priv/Billables/factories/MaxPricePluginFactory'
import { MinPriceRowPlugin } from '@domains/subscription/priv/Billables/factories/MinPricePluginFactory'
import { TieredPriceRowPlugin } from '@domains/subscription/priv/Billables/factories/TieredPricePluginFactory'
import { BorderedStack } from '@domains/subscription/priv/components/BorderedStack'
import type { ConfirmableProps } from '@domains/subscription/priv/components/Confirmable'
import { ConfirmModal } from '@domains/subscription/priv/components/Confirmable/ConfirmableModal'
import { Stack, useTheme } from '@mui/material'
import { useCallback } from 'react'
import { BillingList } from '../../../components/BillingList'
import type { Billable, BillablePrice, PricePlanTemplate } from '../../../types'
import { BillableForm } from '../BillableForm/BillableForm'

export interface BillablesProps {
  template: PricePlanTemplate
  availableBillables: Billable[]
  onDelete: (billableId: string) => void
  onNavigateToBillable?: (billableId: string) => void
  selectedBillableId: string | undefined
  onCreatedPrice: () => void
  formatBillingFrequency: (paymentFrequency: BillablePrice['paymentFrequency']) => string
  currencies: string[]
}

export const Billables = ({
  template,
  selectedBillableId,
  availableBillables,
  onDelete,
  onNavigateToBillable,
  onCreatedPrice,
  formatBillingFrequency,
  currencies,
}: BillablesProps) => {
  const { palette } = useTheme()

  const selectTemplateBillable = (billableId: string) => onNavigateToBillable?.(billableId)
  const selectBillable = (billableId: string) => onNavigateToBillable?.(billableId)

  const configuredBillables = availableBillables.filter((billable) => template.billables.some((b) => b.billableId === billable.id))
  const unconfiguredBillables = availableBillables.filter((billable) => !template.billables.some((b) => b.billableId === billable.id))

  const selectedBillablePrice = template.billables.find((b) => b.billableId === selectedBillableId)
  const selectedBillable = availableBillables.find((b) => b.id === selectedBillableId)

  const Confirmable = useCallback(
    <T,>(props: ConfirmableProps<T>) => {
      return <ConfirmModal connections={template.numberOfConnections} {...props} />
    },
    [template.numberOfConnections],
  )

  const shouldConfirm = template.numberOfConnections > 0

  return (
    <Stack
      sx={{
        flex: 1,
        justifyContent: 'space-between',
        alignItems: 'stretch',
        flexDirection: 'row',
        minHeight: 0,
        overflow: 'hidden',
        gap: 2,
      }}
    >
      <BorderedStack
        sx={{
          overflow: 'auto',
          flex: 1,
          padding: 3,
          gap: 4,
          bgcolor: palette.common.white,
        }}
      >
        <BillingList
          activeRowId={selectedBillableId}
          sections={[
            {
              title: 'Configured billables',
              items: configuredBillables,
              onClick: selectTemplateBillable,
              active: true,
            },
            {
              title: 'Unconfigured billables',
              items: unconfiguredBillables,
              onClick: selectBillable,
              active: false,
            },
          ]}
        />
      </BorderedStack>
      <Stack
        sx={{
          position: 'sticky',
          top: (theme) => theme.spacing(2),
          flex: 1,
        }}
      >
        {selectedBillable && (
          <BorderedStack
            sx={{
              padding: 3,
              gap: 4,
              bgcolor: palette.common.white,
            }}
          >
            <BillableForm
              title={selectedBillable.name}
              billableId={selectedBillable.id}
              billablePrice={selectedBillablePrice}
              onDelete={onDelete}
              Confirmable={shouldConfirm ? Confirmable : undefined}
              templateId={template.id}
              currencies={currencies}
              onCreatedPrice={onCreatedPrice}
              formatPaymentFrequency={formatBillingFrequency}
              unit={selectedBillable.unit}
              plugins={[
                new LinearPriceRowPlugin(),
                new MaxPriceRowPlugin([new LinearPriceRowPlugin()]),
                new MinPriceRowPlugin([new LinearPriceRowPlugin()]),
                new TieredPriceRowPlugin([
                  new LinearPriceRowPlugin(),
                  new MaxPriceRowPlugin([new LinearPriceRowPlugin()]),
                  new MinPriceRowPlugin([new LinearPriceRowPlugin()]),
                ]),
              ]}
              onSuccessSave={onCreatedPrice}
            />
          </BorderedStack>
        )}
      </Stack>
    </Stack>
  )
}
